import { useEffect, useRef, useState } from "react";
import { api } from "./api";

const SITE = typeof window !== "undefined" ? window.location.origin : "";

function setMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

export function useSeo({ title, description, path, jsonLd, noindex }) {
  useEffect(() => {
    const full = title ? `${title} | Casa C'è` : "Casa C'è";
    document.title = full;
    if (description) {
      setMeta("name", "description", description);
      setMeta("property", "og:description", description);
    }
    setMeta("property", "og:title", full);
    setMeta("name", "robots", noindex ? "noindex,nofollow" : "index,follow");
    const url = `${SITE}${path ?? window.location.pathname}`;
    setMeta("property", "og:url", url);
    let link = document.head.querySelector('link[rel="canonical"]');
    if (link) link.setAttribute("href", url);
    let s = document.getElementById("page-jsonld");
    if (s) s.remove();
    if (jsonLd) {
      s = document.createElement("script");
      s.type = "application/ld+json";
      s.id = "page-jsonld";
      s.text = JSON.stringify(jsonLd);
      document.head.appendChild(s);
    }
  }, [title, description, path, jsonLd, noindex]);
}

export function useFetch(url, deps = []) {
  const [state, setState] = useState({ data: null, loading: true, error: null });
  useEffect(() => {
    let alive = true;
    setState((s) => ({ ...s, loading: true, error: null }));
    api.get(url).then(
      (r) => alive && setState({ data: r.data, loading: false, error: null }),
      (e) => alive && setState({ data: null, loading: false, error: e })
    );
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, ...deps]);
  return state;
}

export function useInView(options = { rootMargin: "0px 0px -10% 0px" }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || !("IntersectionObserver" in window)) { setVisible(true); return; }
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); io.disconnect(); } }, options);
    io.observe(el);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return [ref, visible];
}
